/**
 * AppHeader Component
 * Complete navigation header extracted from App.js
 * Includes navigation tabs, quick actions, and user menu
 */
import React from 'react';
import {
  FileText,
  FolderOpen,
  LogOut,
  User,
  Users,
  Database,
  Building,
  Settings,
  Factory,
  Wrench,
  History,
  Layers,
  Square
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { PriceAlertBadge } from '@/components/admin/PriceAlertsManager';

const AppHeader = ({
  activeView,
  setActiveView,
  currentUser,
  isAdmin,
  onLogout,
  onOpenBackupManager,
  onOpenProjectsManager,
  onOpenCompanySettings,
  onOpenUserManagement,
  onOpenSeriesManager,
  onOpenPriceAlerts
}) => {
  return (
    <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-slate-900 rounded-sm flex items-center justify-center">
              <Square className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-slate-900 tracking-tight">AluMetric</h1>
              <p className="text-xs text-slate-500">Lista de Precio Venta</p>
            </div>
          </div>
          
          {/* Navigation Tabs */}
          <div className="flex items-center gap-1">
            <Button
              data-testid="nav-main"
              variant={activeView === "main" ? "default" : "ghost"}
              size="sm"
              onClick={() => setActiveView("main")}
              className="flex items-center gap-2"
            >
              <Layers className="w-4 h-4" />
              Lista de Precio Venta
            </Button>
            <Button
              data-testid="nav-quotation"
              variant={activeView === "quotation" ? "default" : "ghost"}
              size="sm"
              onClick={() => setActiveView("quotation")}
              className="flex items-center gap-2"
            >
              <FileText className="w-4 h-4" />
              Cotización
            </Button>
            <Button
              data-testid="nav-factory"
              variant={activeView === "factory" ? "default" : "ghost"}
              size="sm"
              onClick={() => setActiveView("factory")}
              className="flex items-center gap-2"
            >
              <Factory className="w-4 h-4" />
              Fábrica
            </Button>
            <Button
              data-testid="nav-maintenance"
              variant={activeView === "maintenance" ? "default" : "ghost"}
              size="sm"
              onClick={() => setActiveView("maintenance")}
              className="flex items-center gap-2"
            >
              <Wrench className="w-4 h-4" />
              Mantenimiento
            </Button>
            <Button
              data-testid="nav-historical"
              variant={activeView === "historical" ? "default" : "ghost"}
              size="sm"
              onClick={() => setActiveView("historical")}
              className="flex items-center gap-2"
            >
              <History className="w-4 h-4" />
              Histórico
            </Button>
            
            {/* Quick Action Buttons */}
            <Button
              data-testid="nav-backup"
              variant="ghost"
              size="sm"
              onClick={onOpenBackupManager}
              className="flex items-center gap-2 text-slate-600"
              title="Administrar Backups"
            >
              <Database className="w-4 h-4" />
            </Button>
            <Button
              data-testid="nav-projects"
              variant="ghost"
              size="sm"
              onClick={onOpenProjectsManager}
              className="flex items-center gap-2 text-slate-600"
              title="Gestión de Proyectos"
            >
              <FolderOpen className="w-4 h-4" />
            </Button>
            <Button
              data-testid="nav-company-settings"
              variant="ghost"
              size="sm"
              onClick={onOpenCompanySettings}
              className="flex items-center gap-2 text-slate-600"
              title="Configuración de Empresa"
            >
              <Building className="w-4 h-4" />
            </Button>
            {isAdmin && (
              <Button
                data-testid="nav-users"
                variant="ghost"
                size="sm"
                onClick={onOpenUserManagement}
                className="flex items-center gap-2 text-slate-600"
                title="Gestión de Usuarios"
              >
                <Users className="w-4 h-4" />
              </Button>
            )}
            <PriceAlertBadge onClick={onOpenPriceAlerts} />
          </div>
          
          {/* Series Button */}
          <Button
            data-testid="add-series-btn"
            variant="outline"
            size="sm"
            onClick={onOpenSeriesManager}
            className="flex items-center gap-2"
          >
            <Settings className="w-4 h-4" />
            Series
          </Button>
          
          {/* User Menu */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="flex items-center gap-2">
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                  <User className="w-4 h-4 text-blue-600" />
                </div>
                <span className="text-sm font-medium text-slate-700 hidden sm:inline">
                  {currentUser?.name || currentUser?.email}
                </span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <div className="px-2 py-1.5 text-sm text-slate-500">
                {currentUser?.email}
              </div>
              <div className="px-2 py-1 text-xs text-slate-400">
                {currentUser?.role === 'admin' ? 'Administrador' : 'Usuario'}
              </div>
              <DropdownMenuSeparator />
              <DropdownMenuItem 
                onClick={onLogout}
                className="text-red-600 cursor-pointer"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Cerrar sesión
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
};

export default AppHeader;
